import { supabase } from "@/lib/supabase";
import { resolveApiBaseUrl } from "@/lib/api-base-url";

const API_BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL as string | undefined,
  window.location.origin,
);

type ApiRequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiFetch(path: string, options: ApiRequestOptions = {}) {
  const token = await getAccessToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const text = await response.text();
    if (text && !contentType.includes("text/html")) {
      let parsedMessage: string | null = null;
      try {
        const parsed = JSON.parse(text) as { message?: unknown };
        if (typeof parsed.message === "string" && parsed.message.trim()) {
          parsedMessage = parsed.message.trim();
        }
      } catch {
        // Fall through to raw text below.
      }
      throw new Error(parsedMessage || text);
    }
    throw new Error(
      `A server error occurred (HTTP ${response.status}). This is likely temporary, please refresh or try again in a moment.`,
    );
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export type ApiAgent = {
  id: string;
  user_id: string;
  name: string;
  role: string | null;
  personality: string | null;
  instructions: string | null;
  examples: string | null;
  guardrails: string | null;
  runtime_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ApiTask = {
  id: string;
  creator_id: string;
  name: string;
  description: string | null;
  status:
    | "TODO"
    | "READY"
    | "IN_PROGRESS"
    | "WAITING_FOR_USER_INPUT"
    | "READY_FOR_REVIEW"
    | "DONE"
    | "ARCHIVED";
  priority: number;
  eligible_agent_ids: string[];
  selected_folder_id: string | null;
  assigned_agent_instance_id: string | null;
  working_directory: string | null;
  created_at: string;
  updated_at: string;
};

export type ApiManagerFolder = {
  id: string;
  label: string;
  display_path: string;
  is_git_repo: boolean;
  local_agent_manager_id?: string;
  created_at: string;
};

export type ApiChat = {
  id: string;
  user_id: string;
  agent_id: string;
  created_at: string;
};

export type ApiChatMessage = {
  id: string;
  chat_id: string;
  sender_type: "USER" | "AGENT";
  sender_id: string;
  message: string;
  created_at: string;
};

export type ApiTaskCheckpoint = {
  id: string;
  task_id: string;
  agent_id: string | null;
  agent_instance_id: string | null;
  checkpoint_type: string | null;
  checkpoint_text: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export type ApiAgentRuntime = {
  id: string;
  execution_mode: "LOCAL" | "CLOUD";
  provider:
    | "ANTIGRAVITY_CLI"
    | "CODEX"
    | "GEMINI_CLI"
    | "CLAUDE_CODE"
    | "HERMES"
    | "OPENCODE";
  model: string | null;
  command_template: string | null;
  timeout_seconds: number | null;
  created_at: string;
  updated_at: string;
};

export type ApiAgentLesson = {
  id: string;
  agent_persona_id: string;
  lesson: string;
  source: string;
  source_type: "SYNCED" | "MANUAL";
  confidence: number;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  persona_name: string | null;
};

export type ApiAgentInstance = {
  id: string;
  status: string;
  current_task_id: string | null;
  local_agent_manager_id: string | null;
  manager_subdomain: string | null;
  slot_index: number | null;
  updated_at: string;
  agent_id: string;
  agent: { id: string; name: string; role: string | null } | null;
  task: { id: string; name: string; status: string } | null;
  task_relation: "current" | "recent" | null;
};

export type ApiAgentInstancesPage = {
  data: ApiAgentInstance[];
  page: {
    pageSize: number;
    offset: number;
    count: number;
    total?: number;
    hasNext: boolean;
  };
};

export type ApiManagerStatus = {
  id: string;
  online?: boolean;
  subdomain?: string | null;
  currentRunningAgents?: number;
  maxParallelAgents?: number;
  cpuPercent?: number;
  memoryFreeMB?: number;
  memoryTotalMB?: number;
  managerVersion?: string | null;
  platform?: string | null;
  installChannel?: string | null;
  reauthRequired?: boolean;
  reauthSessionId?: string | null;
  upgrade?: {
    latestVersion: string;
    minimumVersion: string | null;
    upgradeAvailable: boolean;
    upgradeRequired: boolean;
    upgradeCommand: string;
    upgradeLabel: string;
  } | null;
};

export type PaginatedResponse<T> = {
  data: T[];
  page: {
    limit: number;
    offset: number;
    total: number;
    hasNext: boolean;
    hasPrev: boolean;
    nextOffset: number | null;
    prevOffset: number | null;
  };
};

export type ApiMorningSummary = {
  executiveSummary: string;
  priorityActions: Array<{
    priority: "critical" | "high" | "medium";
    title: string;
    agentLabel?: string;
    taskStatus?:
      | "TODO"
      | "READY"
      | "IN_PROGRESS"
      | "WAITING_FOR_USER_INPUT"
      | "READY_FOR_REVIEW"
      | "DONE";
    kind: "security" | "design" | "release" | "review" | "ops" | "general";
  }>;
  generatedAt: string;
  windowStart: string;
  windowEnd: string;
  timeZone: string;
  taskCount: number;
  activeAgentCount: number;
  completedCount: number;
  inProgressCount: number;
  needsInputCount: number;
  autoRate: number;
};

export type BillingSubscriptionStatus = {
  status: string;
  has_access: boolean;
  current_period_end: string | null;
  grace_period_ends_at?: string | null;
  trial_ends_at?: string | null;
  is_trial?: boolean;
  read_only?: boolean;
  plan?: "monthly" | "annual" | null;
};

export async function listAgents(): Promise<ApiAgent[]> {
  return apiFetch("/agents");
}

export async function listManagers(): Promise<ApiManagerStatus[]> {
  return apiFetch("/api/managers");
}

export async function listAgentInstances(params: {
  pageSize: number;
  offset?: number;
  category?: "active" | "offline" | "all";
}): Promise<ApiAgentInstancesPage> {
  const query = new URLSearchParams();
  query.set("pageSize", String(params.pageSize));
  query.set("offset", String(params.offset ?? 0));
  query.set("category", params.category ?? "all");
  return apiFetch(`/agents/instances?${query.toString()}`);
}

export async function getAgentInstanceById(
  instanceId: string,
): Promise<ApiAgentInstance> {
  return apiFetch(`/agents/instances/${encodeURIComponent(instanceId)}`);
}

export async function getAgentInstanceForTask(
  taskId: string,
): Promise<ApiAgentInstance> {
  return apiFetch(`/agents/instances/by-task/${encodeURIComponent(taskId)}`);
}

export async function sendCommandToInstance(
  instanceId: string,
  command: string,
): Promise<{ ok: true }> {
  return apiFetch(`/api/agents/${encodeURIComponent(instanceId)}/commands`, {
    method: "POST",
    body: { command },
  });
}

export async function createAgent(payload: {
  name: string;
  role?: string;
  personality?: string;
  instructions?: string;
  examples?: string;
  guardrails?: string;
  runtimeId?: string | null;
}): Promise<ApiAgent> {
  return apiFetch("/agents", { method: "POST", body: payload });
}

export async function listAgentRuntimes(): Promise<
  PaginatedResponse<ApiAgentRuntime>
> {
  return apiFetch("/agent-runtimes");
}

export async function updateAgent(
  id: string,
  payload: {
    name?: string;
    role?: string;
    personality?: string;
    instructions?: string;
    examples?: string;
    guardrails?: string;
    runtimeId?: string | null;
  },
): Promise<ApiAgent> {
  return apiFetch(`/agents/${id}`, { method: "PUT", body: payload });
}

export async function deleteAgent(id: string): Promise<{ ok: true }> {
  return apiFetch(`/agents/${id}`, { method: "DELETE" });
}

export async function listTasks(
  params: {
    status?: ApiTask["status"];
    agentId?: string;
    priority?: number;
    includeArchived?: boolean;
  } = {},
): Promise<ApiTask[]> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.agentId) query.set("agentId", params.agentId);
  if (params.priority !== undefined)
    query.set("priority", String(params.priority));
  if (params.includeArchived) query.set("includeArchived", "true");
  const suffix = query.toString();
  return apiFetch(`/tasks${suffix ? `?${suffix}` : ""}`);
}

export async function createTask(payload: {
  name: string;
  description?: string;
  priority?: number;
  eligibleAgentIds?: string[];
  selectedFolderId?: string | null;
}): Promise<ApiTask> {
  return apiFetch("/tasks", { method: "POST", body: payload });
}

export async function updateTask(
  id: string,
  payload: {
    name?: string;
    description?: string;
    priority?: number;
    status?: ApiTask["status"];
    eligibleAgentIds?: string[];
    selectedFolderId?: string | null;
  },
): Promise<ApiTask> {
  return apiFetch(`/tasks/${id}`, { method: "PUT", body: payload });
}

export async function listManagerFolders(): Promise<ApiManagerFolder[]> {
  return apiFetch("/manager-folders");
}

export async function createManagerFolder(
  managerId: string,
  payload: {
    displayPath: string;
    label?: string;
    isGitRepo?: boolean;
  },
): Promise<ApiManagerFolder> {
  return apiFetch(`/api/managers/${encodeURIComponent(managerId)}/folders`, {
    method: "POST",
    body: payload,
  });
}

export async function deleteManagerFolder(
  managerId: string,
  folderId: string,
): Promise<{ ok: true }> {
  return apiFetch(
    `/api/managers/${encodeURIComponent(managerId)}/folders/${encodeURIComponent(folderId)}`,
    {
      method: "DELETE",
    },
  );
}

export async function deleteTask(id: string): Promise<{ ok: true }> {
  return apiFetch(`/tasks/${id}`, { method: "DELETE" });
}

export async function listTaskCheckpoints(
  taskId: string,
): Promise<ApiTaskCheckpoint[]> {
  return apiFetch(`/tasks/${encodeURIComponent(taskId)}/task_checkpoints`);
}

export async function listChats(): Promise<ApiChat[]> {
  return apiFetch("/chats");
}

export async function createChat(payload: {
  agentId: string;
}): Promise<ApiChat> {
  return apiFetch("/chats", { method: "POST", body: payload });
}

export async function deleteChat(id: string): Promise<{ ok: true }> {
  return apiFetch(`/chats/${id}`, { method: "DELETE" });
}

export async function listChatMessages(
  chatId: string,
  params: { fromTimestamp?: string; toTimestamp?: string; limit?: number } = {},
): Promise<ApiChatMessage[]> {
  const query = new URLSearchParams();
  if (params.fromTimestamp) query.set("fromTimestamp", params.fromTimestamp);
  if (params.toTimestamp) query.set("toTimestamp", params.toTimestamp);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return apiFetch(`/chats/${chatId}/messages${suffix ? `?${suffix}` : ""}`);
}

export async function createChatMessage(
  chatId: string,
  payload: { message: string },
): Promise<ApiChatMessage> {
  return apiFetch(`/chats/${chatId}/messages`, {
    method: "POST",
    body: payload,
  });
}

export async function listAgentLessons(): Promise<ApiAgentLesson[]> {
  return apiFetch("/agent-lessons");
}

export async function listAgentLessonTimeline(): Promise<ApiAgentLesson[]> {
  return apiFetch("/agent-lessons/timeline");
}

export async function createManualAgentLesson(payload: {
  agentPersonaId: string;
  lesson: string;
}): Promise<ApiAgentLesson> {
  return apiFetch("/agent-lessons/manual", {
    method: "POST",
    body: payload,
  });
}

export async function updateManualAgentLesson(
  lessonId: string,
  payload: { lesson: string },
): Promise<ApiAgentLesson> {
  return apiFetch(`/agent-lessons/${lessonId}`, {
    method: "PATCH",
    body: {
      lesson: payload.lesson,
    },
  });
}

export async function deleteAgentLesson(
  lessonId: string,
): Promise<{ ok: true }> {
  return apiFetch(`/agent-lessons/${lessonId}`, {
    method: "DELETE",
  });
}

export async function getMorningSummary(
  timeZone: string,
): Promise<ApiMorningSummary> {
  const query = new URLSearchParams();
  query.set("timeZone", timeZone);
  return apiFetch(`/morning-summary?${query.toString()}`);
}

export async function createCheckoutSession(opts?: {
  plan?: "monthly" | "annual";
}): Promise<{ url: string }> {
  return apiFetch("/billing/create-checkout-session", {
    method: "POST",
    body: opts ?? {},
  });
}

export async function createPortalSession(): Promise<{ url: string }> {
  return apiFetch("/billing/create-portal-session", { method: "POST" });
}

export async function getSubscriptionStatus(): Promise<BillingSubscriptionStatus> {
  return apiFetch("/billing/subscription-status");
}

export async function startTrial(): Promise<void> {
  await apiFetch("/billing/start-trial", { method: "POST" });
}
